/*
 * EmptyScreen — agent ran but found no verified match. Lifted from
 * ui-handoff/.../app.jsx::EmptyScreen, parameterised on the trace events that
 * the agent actually emitted so the "what was tried" panel reflects reality
 * instead of canned prototype lines.
 */

import React from 'react';

function summariseTrace(events) {
  const summary = [];
  for (const event of events) {
    if (event.kind === 'tool_call') {
      const outCount = (event.output || []).length;
      summary.push({
        label: event.name,
        detail: outCount > 0 ? event.output[0] : event.args,
      });
    } else if (event.kind === 'plan') {
      summary.push({ label: 'plan', detail: event.title });
    }
  }
  return summary;
}

export function EmptyScreen({ query, traceEvents, onNewSearch, onRefine }) {
  const tried = summariseTrace(traceEvents);
  return (
    <div className="empty fade-in">
      <div className="empty-mark serif">∅</div>
      <div className="mono micro" style={{ color: 'var(--ink-3)' }}>NO MATCH</div>
      <h2 className="empty-h serif">
        Agent ran end-to-end and didn't find a clip that meets the verification bar.
      </h2>
      <p className="empty-q">
        Query: <em>"{query}"</em>
      </p>
      {tried.length > 0 && (
        <div className="empty-detail">
          <div className="empty-detail-hd mono">what was tried</div>
          <ul>
            {tried.map((row, i) => (
              <li key={i}>
                <span className="mono">{row.label}</span> {row.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="empty-actions">
        <button type="button" className="empty-btn-primary mono" onClick={onRefine}>
          refine query
        </button>
        <button type="button" className="empty-btn-ghost mono" onClick={onNewSearch}>
          start over
        </button>
      </div>
      <style>{`
        .empty { max-width: 720px; margin: 0 auto; padding: 88px 32px 64px; display: flex; flex-direction: column; gap: 16px; }
        .empty-mark { font-size: 72px; color: var(--ink-4); line-height: 1; margin-bottom: 4px; }
        .empty-h { font-size: 32px; line-height: 1.25; font-weight: 400; color: var(--ink); margin: 0; max-width: 580px; text-wrap: balance; }
        .empty-q { color: var(--ink-2); font-size: 15px; margin: 8px 0 12px; font-family: var(--serif); }
        .empty-q em { color: var(--accent); }
        .empty-detail { background: var(--panel-2); border-left: 2px solid var(--ink-4); padding: 14px 18px; margin-top: 12px; }
        .empty-detail-hd { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3); margin-bottom: 8px; }
        .empty-detail ul { margin: 0; padding-left: 16px; color: var(--ink-2); font-size: 13.5px; line-height: 1.7; }
        .empty-detail .mono { color: var(--accent); font-size: 12.5px; margin-right: 6px; }
        .empty-actions { display: flex; gap: 10px; margin-top: 16px; }
        .empty-btn-primary, .empty-btn-ghost { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; padding: 9px 18px; border: 1px solid var(--ink); }
        .empty-btn-primary { background: var(--ink); color: var(--bg); }
        .empty-btn-primary:hover { background: var(--accent); border-color: var(--accent); }
        .empty-btn-ghost { background: transparent; color: var(--ink); border-color: var(--rule-2); }
        .empty-btn-ghost:hover { border-color: var(--ink); }
      `}</style>
    </div>
  );
}
